import { useState, useCallback } from "react"
import { lumi } from "../lib/lumi"

// ==================== 타입 정의 ====================

export interface Announcement {
  _id: string
  title: string
  content: string
  active: boolean
  publishedAt?: string
  revision: number
  creator: string
  creatorName?: string
  createdAt: string
  updatedAt: string
}

export interface AnnouncementInput {
  title: string
  content: string
  active: boolean
  publishedAt?: string
}

// ==================== dismiss 상태 (localStorage) ====================

/**
 * 공지 dismiss 키.
 * revision을 포함하므로, 관리자가 "수정 내용을 다시 알림"으로 revision을 올리면
 * 이미 '다시 보지 않기'를 누른 사용자에게도 다시 노출된다.
 */
export function getDismissKey(id: string, revision: number): string {
  return `announcement_dismissed_${id}_r${revision}`
}

/** 해당 공지(revision 포함)를 사용자가 '다시 보지 않기'로 처리했는지 */
export function isAnnouncementDismissed(id: string, revision: number): boolean {
  try {
    return localStorage.getItem(getDismissKey(id, revision)) === "true"
  } catch {
    // localStorage 접근 불가 환경에서는 항상 표시
    return false
  }
}

/** '다시 보지 않기' 저장 (닫기에서는 호출하지 않음) */
export function dismissAnnouncement(id: string, revision: number): void {
  try {
    localStorage.setItem(getDismissKey(id, revision), "true")
  } catch {
    // 저장 실패는 무시 (기능 비필수)
  }
}

// ==================== 표시용 유틸 ====================

/** 화면 표시용 날짜 (YYYY.MM.DD) */
export function formatAnnouncementDate(iso?: string): string {
  if (!iso) return "-"
  const parts = iso.slice(0, 10).split("-")
  if (parts.length < 3) return "-"
  return `${parts[0]}.${parts[1]}.${parts[2]}`
}

/** date input 값(YYYY-MM-DD) → ISO datetime */
export function dateInputToIso(value: string): string {
  if (!value) return ""
  return new Date(`${value}T00:00:00.000Z`).toISOString()
}

/** ISO datetime → date input 값(YYYY-MM-DD) */
export function isoToDateInput(value?: string): string {
  if (!value) return ""
  return value.slice(0, 10)
}

// ==================== 훅 ====================

export function useAnnouncements() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(false)

  /**
   * 활성 공지 중 최신 1건만 조회 (팝업 전용).
   * 홈/전역 진입 부담을 줄이기 위해 limit 1로 제한한다.
   */
  const fetchActiveAnnouncement = useCallback(async (): Promise<Announcement | null> => {
    try {
      const { list } = await lumi.entities.announcements.list({
        filter: { active: true },
        sort: { createdAt: -1 },
        limit: 1,
      })
      const found = (list || [])[0]
      return found ? (found as Announcement) : null
    } catch (error) {
      console.error("활성 공지 조회 실패:", error)
      return null
    }
  }, [])

  /** 관리자용 전체 공지 목록 조회 */
  const fetchAnnouncements = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.announcements.list({
        sort: { createdAt: -1 },
      })
      setAnnouncements((list || []) as Announcement[])
    } catch (error) {
      console.error("공지 목록 조회 실패:", error)
      setAnnouncements([])
    } finally {
      setLoading(false)
    }
  }, [])

  /** 현재 활성 공지 전체를 비활성 처리 (활성 공지는 항상 1건만 유지) */
  const deactivateOthers = useCallback(async (exceptId?: string) => {
    try {
      const { list } = await lumi.entities.announcements.list({
        filter: { active: true },
      })
      const targets = (list || []).filter((a: any) => a._id !== exceptId)
      const now = new Date().toISOString()
      await Promise.all(
        targets.map((a: any) =>
          lumi.entities.announcements.update(a._id, { active: false, updatedAt: now })
        )
      )
    } catch (error) {
      console.error("기존 활성 공지 비활성 처리 실패:", error)
      throw error
    }
  }, [])

  /** 공지 생성 */
  const createAnnouncement = useCallback(
    async (input: AnnouncementInput, creator: string, creatorName?: string) => {
      const now = new Date().toISOString()
      // 활성으로 등록하는 경우 기존 활성 공지를 먼저 비활성 처리
      if (input.active) {
        await deactivateOthers()
      }
      const created = await lumi.entities.announcements.create({
        title: input.title,
        content: input.content,
        active: Boolean(input.active),
        publishedAt: input.publishedAt || now,
        revision: 1,
        creator,
        creatorName: creatorName || "",
        createdAt: now,
        updatedAt: now,
      })
      return created
    },
    [deactivateOthers]
  )

  /**
   * 공지 수정 (ID 먼저, 데이터 나중).
   * bumpRevision이 true면 revision을 올려 모든 사용자에게 다시 노출한다.
   */
  const updateAnnouncement = useCallback(
    async (target: Announcement, input: AnnouncementInput, bumpRevision: boolean) => {
      const now = new Date().toISOString()
      if (input.active) {
        await deactivateOthers(target._id)
      }
      const payload: Record<string, any> = {
        title: input.title,
        content: input.content,
        active: Boolean(input.active),
        publishedAt: input.publishedAt || target.publishedAt || now,
        updatedAt: now,
      }
      if (bumpRevision) {
        payload.revision = Number(target.revision || 1) + 1
      }
      const updated = await lumi.entities.announcements.update(target._id, payload)
      return updated
    },
    [deactivateOthers]
  )

  /** 활성/비활성 토글 (활성화 시 기존 활성 공지는 자동 비활성) */
  const toggleActive = useCallback(
    async (target: Announcement) => {
      const now = new Date().toISOString()
      const next = !target.active
      if (next) {
        await deactivateOthers(target._id)
      }
      await lumi.entities.announcements.update(target._id, { active: next, updatedAt: now })
      return next
    },
    [deactivateOthers]
  )

  /** 공지 삭제 (문자열 ID만) */
  const deleteAnnouncement = useCallback(async (id: string) => {
    await lumi.entities.announcements.delete(id)
  }, [])

  return {
    announcements,
    loading,
    fetchActiveAnnouncement,
    fetchAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    toggleActive,
    deleteAnnouncement,
  }
}
